import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import DocumentUtils from './document-utils';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

/*
    Utility service class used to add functionality to documents.
    As of July 2026, this is mostly used for the Molecular landing pages
*/
@Injectable()
export class RenderingUtils {
    private renderer: Renderer2;
    constructor(rendererFactory: RendererFactory2, private translationService: TranslateService) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public initializeCollapsibleSections(): void {
        const intersectionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const collapsibleToggle = entry.target.querySelector('.collapsable-toggle') as HTMLElement;

                if (collapsibleToggle) {
                  collapsibleToggle.classList.add('minus-black');
                  if (!collapsibleToggle.dataset.listenerAdded) {
                      this.renderer.listen(collapsibleToggle, 'click', () => {
                          DocumentUtils.updateElementClass(collapsibleToggle, 'plus-black', 'minus-black');
                          this.toggleSectionVisibility(collapsibleToggle);
                      });
                      collapsibleToggle.dataset.listenerAdded = 'true'; // Mark as listener added
                  }
                }
            });
        });

        const observeCollapsableSection = () => {
            document.querySelectorAll('.collapsablesection').forEach((collapsablePanel) => {
                intersectionObserver.observe(collapsablePanel);
            });
        };
        observeCollapsableSection();

        const mutationObserver = new MutationObserver(() => {
            observeCollapsableSection(); // Re-observe if mutations occur
        });

        mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    /*
        Toggles whether a section is collapsed or expanded
    */
    private toggleSectionVisibility(e: HTMLElement): void {
        const sectionHeading = e.nextSibling;
        if (sectionHeading) {
            let sectionElement = sectionHeading.nextSibling as HTMLElement;

            while (sectionElement) {
                DocumentUtils.updateElementClass(sectionElement, 'collapsed', 'collapsable-body')
                sectionElement = sectionElement.nextSibling as HTMLElement;
            }
        }
    }

    /*
        Initialize a molecular landing page, which has tables nested in collapsable sections
        Adds click listeners to each of the expandable elements
    */
    initializeCollapsableNestedTable() {
        const expandableSectionsIntersectionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const listHeaderElements = entry.target.querySelectorAll('p.dita-p+ol.dita-ol');
                listHeaderElements.forEach(sectionElement => {
                    const sectionHeaderToggleElement = sectionElement.previousElementSibling as HTMLElement;
                    const sectionBody = sectionElement as HTMLElement;

                    // This check for if the current level has text is needed
                    // For cases where the route/dosage is ommited for redundancy
                    // So we don't create empty collapsibles
                    if (sectionHeaderToggleElement.innerText != '') {
                        sectionHeaderToggleElement.classList.add('cursor-pointer', 'arrow-down-white');
                    }

                    if (!sectionHeaderToggleElement.dataset.listenerAdded) {
                        this.renderer.listen(sectionHeaderToggleElement, 'click', () => {
                            if (sectionBody) {
                                DocumentUtils.updateElementClass(sectionBody, 'hidden');
                                DocumentUtils.updateElementClass(sectionHeaderToggleElement, 'arrow-down-white', 'arrow-right-white');
                            }
                        });
                        sectionHeaderToggleElement.dataset.listenerAdded = 'true'; // Mark as listener added
                    }
                });
            });
            this.initializeCollapseAllButton();
        });

        const observeCollapsableSection = () => {
            document.querySelectorAll('.collapsablesection > .dita-body > ol.dita-ol').forEach((collapsablePanel) => {
                expandableSectionsIntersectionObserver.observe(collapsablePanel);
            });
        };
        //observeCollapsableSection();

        const mutationObserver = new MutationObserver(() => {
            observeCollapsableSection(); // Re-observe if mutations occur
        });

        mutationObserver.observe(document.body, { childList: true, subtree: true });

    }

    initializeCollapseAllButton() {
        let collapseAllQuery = document.querySelectorAll('.toggle-collapse-all');
        collapseAllQuery.forEach(collapseAllButton => {
            const buttonElement = collapseAllButton as HTMLElement;
            if (!collapseAllButton.classList.contains('initialized')) {
                this.renderer.listen(buttonElement, 'click', () => this.toggleCollapseExpandAllChildSections(buttonElement));
                buttonElement.classList.add('initialized');
                this.translationService.get('Labels.collapseAll').pipe(take(1))
                    .subscribe(text => {
                        buttonElement.innerHTML = text;
                    });
            }
        });
    }

    /*
        Collapse/Expand all elements under a table with .chapter-info class
        NOTE: Collapse all will only collapse up to the second level, as there are not many
        elements at the root level (E.G. see the Amoxicillin MLP)
    */
    toggleCollapseExpandAllChildSections(collapseAllButton: HTMLElement): void {
        let isCollapsingAll = collapseAllButton.classList.contains('toggle-collapse-all');
        let parentSection = collapseAllButton.parentElement || collapseAllButton;
        let sectionHead = parentSection.querySelector('ol.dita-ol') || parentSection;
        let topLevelCollapsableSections = sectionHead.querySelectorAll(':scope > li.dita-li');

        // This looks a little silly, but with the way the tables are structured
        // we need to get a handle on each top level element, then set a flag to skip the first
        // element from the querySelectorAll since it includes itself
        topLevelCollapsableSections.forEach(rootSection => {
            const listHeaderElements = rootSection.querySelectorAll('p.dita-p+ol.dita-ol');
            let isTopLevel = isCollapsingAll ? true : false;
            listHeaderElements.forEach(sectionElement => {
                if (!isTopLevel) {
                    const sectionHeaderToggleElement = sectionElement.previousElementSibling as HTMLElement;
                    const sectionBody = sectionElement as HTMLElement;
                    if (isCollapsingAll) {
                        // collapse only if not collapsed
                        if (sectionHeaderToggleElement.classList.contains('arrow-down-white')) {
                            DocumentUtils.updateElementClass(sectionHeaderToggleElement, 'arrow-down-white', 'arrow-right-white');
                            DocumentUtils.updateElementClass(sectionBody, 'hidden');
                        }
                    } else {
                        // expand only if collapsed
                        if (sectionHeaderToggleElement.classList.contains('arrow-right-white')) {
                            DocumentUtils.updateElementClass(sectionHeaderToggleElement, 'arrow-down-white', 'arrow-right-white');
                            DocumentUtils.updateElementClass(sectionBody, 'hidden');
                        }
                    }
                }
                isTopLevel = false;
            });
        });

        const buttonText = isCollapsingAll 
            ? this.translationService.get('Labels.expandAll')
            : this.translationService.get('Labels.collapseAll');

        buttonText.pipe(take(1))
            .subscribe(buttonText => collapseAllButton.innerHTML = buttonText);

        DocumentUtils.updateElementClass(collapseAllButton, 'toggle-collapse-all', 'toggle-expand-all');
    }

}
